import type {
  CandidateInsights,
  CommutePref,
  LanguageSkill,
  PayValue,
  PublicClaim,
  RelocationPref,
  Responsiveness,
  ShiftPref,
  TravelPref,
} from '@factoryfixinc/ats-interfaces';

import { SelectedFilterType } from '@/core/sourcing/search/types/selected-filter-type.type';
import { formatUTCDate } from '@/core/sourcing/utils/dates';

/**
 * Pure, display-only mapping from a served {@link CandidateInsights} payload to the chips the
 * Search card renders. Each field composes from its typed `value` first and falls
 * back to `displayText` only where the product mapping allows it (pay,
 * lastOpenToOpportunities, and the conditional-shift case; commute is typed-only). A field
 * with no derivable label yields no chip; an empty payload yields no chips at all — the
 * component renders nothing then.
 *
 * Every claim earns a chip whatever its confidence, so the card agrees with the filters, which
 * match low-confidence values too (low-confidence pay still reads as an estimate). Chips are
 * assembled in a fixed priority order and colored by {@link ChipCategory}, and the card shows
 * at most {@link MAX_CHIPS} of them. Chips for fields the search filters on go first, so the
 * cap never hides the signal the recruiter asked for.
 *
 * `workAuthorization`, `dealbreakers`, and `certifications` are never surfaced: the first two
 * are never served, and the v1 parser never emits certifications.
 */

// `default` is the flat category-colored pill; `negative` flags an unfavorable signal (a hard
// constraint like "won't relocate" or a poor track record like "slow to respond") and always
// reads caution; `conditional` softens a preference into a caveat.
export type ChipVariant = 'default' | 'negative' | 'conditional';

// Groups fields into the design's four color families. `pay` stands alone; the rest cluster by
// what kind of signal they are — a scheduling/logistics preference, a hiring-intent signal, or a
// language skill.
export type ChipCategory = 'pay' | 'preference' | 'intent' | 'language';

export interface ChipSpec {
  /** Stable v-for key; unique across the returned set (equals `field`). */
  key: string;
  /** Insights field the chip derives from; drives the `data-test-id`. */
  field: ChipField;
  /** Color family the pill renders in. */
  category: ChipCategory;
  label: string;
  /** Muted suffix — the `(est.)` marker or the `· conditional` caveat. */
  note?: string;
  variant: ChipVariant;
  /** `Last updated <month year>` line for the per-chip tooltip; empty when the claim has no date or the field opts out. */
  tooltip: string;
}

// Chip render order on the card, most useful signal first. Fields absent from a payload are
// skipped; whatever survives is sliced to MAX_CHIPS.
const PRIORITY = [
  'pay',
  'shifts',
  'shifts-weekend',
  'commute',
  'relocation',
  'travel',
  'responsiveness',
  'roleInterests',
  'targetIndustries',
  'lastOpenToOpportunities',
  'languages',
] as const;

export type ChipField = (typeof PRIORITY)[number];

// Cap held to 7 for now; may be lowered after ISO review.
const MAX_CHIPS = 7;

// Insight filter → the chip that shows its field. The pay floor filters nothing, so only the
// ceiling counts.
const FIELD_BY_FILTER: Partial<Record<SelectedFilterType, ChipField>> = {
  [SelectedFilterType.PAY_RATE_MAX]: 'pay',
  [SelectedFilterType.SHIFT]: 'shifts',
  [SelectedFilterType.WILLING_TO_RELOCATE]: 'relocation',
  [SelectedFilterType.OPEN_TO_TRAVEL]: 'travel',
  [SelectedFilterType.LANGUAGE]: 'languages',
  [SelectedFilterType.RESPONSIVENESS]: 'responsiveness',
  [SelectedFilterType.LAST_OPEN_TO_OPPORTUNITIES]: 'lastOpenToOpportunities',
};

/** The chip fields whose insight filter is active, for {@link buildInsightChips}. */
export function filteredInsightFields(
  isActive: (type: SelectedFilterType) => boolean,
): Set<ChipField> {
  return new Set(
    Object.entries(FIELD_BY_FILTER).flatMap(([type, field]) =>
      field && isActive(type as SelectedFilterType) ? [field] : [],
    ),
  );
}

// Field → color family, applied centrally in finalizeChip so drafts never need to carry it.
const FIELD_CATEGORY: Record<ChipField, ChipCategory> = {
  pay: 'pay',
  shifts: 'preference',
  'shifts-weekend': 'preference',
  commute: 'preference',
  relocation: 'preference',
  travel: 'preference',
  responsiveness: 'intent',
  roleInterests: 'intent',
  targetIndustries: 'intent',
  lastOpenToOpportunities: 'intent',
  languages: 'language',
};

const MIDDOT = '·'; // ·
const ENDASH = '–'; // – (pay ranges)

const SHIFT_ORDINALS = { first: '1st', second: '2nd', third: '3rd' } as const;

// English is the assumed baseline, so it never earns a language chip on its own.
const ENGLISH_ALIASES = new Set(['english', 'en']);

// A draft carries a field's own copy and any intrinsic variant; category and the tooltip are
// layered on centrally from the field name and source claim in `finalizeChip`.
interface ChipDraft {
  field: ChipField;
  label: string;
  note?: string;
  variant?: Exclude<ChipVariant, 'default'>;
}

function trimmedOrNull(text?: string): string | null {
  const trimmed = text?.trim();
  return trimmed ? trimmed : null;
}

// First occurrence wins; comparison is case-insensitive but the surviving casing is preserved.
function dedupeCaseInsensitive(values: string[]): string[] {
  const seen = new Set<string>();
  return values.filter((value) => {
    const key = value.toLowerCase();
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

// Both formatted in UTC: served dates are midnight-UTC calendar stamps, and local formatting
// would shift them a day (and possibly a month) back west of UTC.

// Tooltip freshness stamp, e.g. "March 2026".
function fullMonthYear(dateString?: string): string | null {
  if (!dateString) {
    return null;
  }
  return formatUTCDate(dateString, 'MMMM YYYY') || null;
}

// Short chip stamp, e.g. "Oct '25".
function shortMonthYear(dateString?: string): string | null {
  if (!dateString) {
    return null;
  }
  return formatUTCDate(dateString, "MMM [']YY") || null;
}

// The "Open to work" chip already carries its own date in the label, so a last-updated date on
// hover would just read as a second, conflicting date — this field opts out of the tooltip.
const NO_TOOLTIP_FIELDS = new Set<ChipField>(['lastOpenToOpportunities']);

// The tooltip is just the freshness date; recruiters read staleness off the date itself, so no
// provenance line and no "may be outdated" warning. A dateless claim yields no tooltip at all.
function buildTooltip(asOf: string | undefined): string {
  const asOfMonth = fullMonthYear(asOf);
  return asOfMonth ? `Last updated ${asOfMonth}` : '';
}

function finalizeChip(draft: ChipDraft, claim: PublicClaim<unknown>): ChipSpec {
  return {
    key: draft.field,
    field: draft.field,
    category: FIELD_CATEGORY[draft.field],
    label: draft.label,
    note: draft.note,
    variant: draft.variant ?? 'default',
    tooltip: NO_TOOLTIP_FIELDS.has(draft.field) ? '' : buildTooltip(claim.asOf),
  };
}

function formatPayRange(value: PayValue): string | null {
  const { min, max, period } = value;
  if (min == null && max == null) {
    return null;
  }
  const low = min ?? (max as number);
  const high = max ?? (min as number);

  if (period === 'year') {
    // A sub-$1k figure cannot be a real annual salary — it's a mis-classified parse
    // (e.g. an hourly range that caught a stray /year marker); rounding it would
    // render "$0k/yr", so refuse the value if either bound is that small.
    if (low < 1000 || high < 1000) {
      return null;
    }
    const k = (amount: number) => Math.round(amount / 1000);
    const lowK = k(low);
    const highK = k(high);
    return lowK === highK ? `$${lowK}k/yr` : `$${lowK}${ENDASH}${highK}k/yr`;
  }
  return low === high ? `$${low}/hr` : `$${low}${ENDASH}${high}/hr`;
}

// Firm ("Target:") vs soft ("Likely … (est.)") pay. Basis wins; absent a basis, low confidence
// reads as an estimate and anything else reads as firm.
function isEstimatedPay(claim: PublicClaim<PayValue>): boolean {
  if (claim.basis === 'stated') {
    return false;
  }
  if (claim.basis === 'estimated' || claim.basis === 'inferred') {
    return true;
  }
  return claim.confidence === 'low';
}

function buildPayDrafts(claim: PublicClaim<PayValue>): ChipDraft[] {
  const range = claim.value ? formatPayRange(claim.value) : null;
  if (!range) {
    const text = trimmedOrNull(claim.displayText);
    return text ? [{ field: 'pay', label: text }] : [];
  }
  // The as-of date is not stamped into the label; freshness lives in the note/tooltip.
  if (isEstimatedPay(claim)) {
    return [{ field: 'pay', label: `Likely ${range}`, note: '(est.)' }];
  }
  return [{ field: 'pay', label: `Target: ${range}` }];
}

function buildShiftOrdinalLabel(pref: ShiftPref): { label: string; variant?: 'negative' } | null {
  const numbered = ['first', 'second', 'third'] as const;
  const acceptable = numbered.filter((k) => pref[k] === 'acceptable').map((k) => SHIFT_ORDINALS[k]);
  const avoided = numbered.filter((k) => pref[k] === 'avoid').map((k) => SHIFT_ORDINALS[k]);

  if (acceptable.length === 1) {
    return { label: `Prefers ${acceptable[0]} shift` };
  }
  if (acceptable.length >= 2) {
    return { label: `Open to ${acceptable.join(' or ')} shift` };
  }
  // Avoiding shifts with none acceptable is a hard scheduling constraint — flag it caution.
  if (avoided.length >= 1) {
    return { label: `Avoids ${avoided.join(' and ')} shift`, variant: 'negative' };
  }
  return null;
}

function buildShiftDrafts(claim: PublicClaim<ShiftPref>): ChipDraft[] {
  const drafts: ChipDraft[] = [];
  const pref = claim.value;

  if (pref?.conditional) {
    // A conditional preference ("would take 3rd if pay is right") can't be reduced to an
    // ordinal, so surface the verbatim displayText with a caveat note — no chip without it.
    const text = trimmedOrNull(claim.displayText);
    if (text) {
      drafts.push({
        field: 'shifts',
        label: text,
        note: `${MIDDOT} conditional`,
        variant: 'conditional',
      });
    }
  } else if (pref) {
    const ordinal = buildShiftOrdinalLabel(pref);
    if (ordinal) {
      drafts.push({ field: 'shifts', label: ordinal.label, variant: ordinal.variant });
    }
  }

  // Weekend avoidance is a hard scheduling constraint, so it earns its own dealbreaker chip
  // regardless of the weekday preference above.
  if (pref?.weekend === 'avoid') {
    drafts.push({
      field: 'shifts-weekend',
      label: "Won't work weekends",
      variant: 'negative',
    });
  }

  return drafts;
}

// Commute is typed-only: its free-text lane carries location listings and hedges
// ("Illinois, potentially San Francisco, CA"), not commute preferences, so a chip
// renders only from a parsed distance until the dossier producer improves the lane.
function buildCommuteLabel(claim: PublicClaim<CommutePref>): string | null {
  const commute = claim.value;
  if (commute?.maxMiles != null) {
    return `Commute-sensitive ${MIDDOT} ~${commute.maxMiles} mi`;
  }
  if (commute?.maxMinutes != null) {
    return `Within ${commute.maxMinutes} min`;
  }
  return null;
}

function buildRelocationDrafts(claim: PublicClaim<RelocationPref>): ChipDraft[] {
  const willing = claim.value?.willing;
  if (willing === true) {
    return [{ field: 'relocation', label: 'Open to relocate' }];
  }
  // A hard "no" is an unfavorable logistics constraint — flag it caution, unlike the neutral "yes".
  if (willing === false) {
    return [{ field: 'relocation', label: 'Not open to relocate', variant: 'negative' }];
  }
  return [];
}

function buildTravelDrafts(claim: PublicClaim<TravelPref>): ChipDraft[] {
  const open = claim.value?.open;
  if (open === true) {
    return [{ field: 'travel', label: 'Open to travel' }];
  }
  // Refusing regular travel is an unfavorable logistics constraint — flag it caution.
  if (open === false) {
    return [{ field: 'travel', label: 'No regular travel', variant: 'negative' }];
  }
  return [];
}

function isEnglish(language: string): boolean {
  return ENGLISH_ALIASES.has(language.trim().toLowerCase());
}

function buildLanguageLabel(claim: PublicClaim<LanguageSkill[]>): string | null {
  const languages = dedupeCaseInsensitive(
    (claim.value ?? [])
      .map((skill) => skill?.language?.trim())
      .filter((language): language is string => !!language),
  );

  // English-only (or empty) says nothing a recruiter doesn't already assume.
  if (!languages.some((language) => !isEnglish(language))) {
    return null;
  }
  if (languages.length === 1) {
    return `Speaks ${languages[0]}`;
  }
  return `Bilingual: ${languages.join(' / ')}`;
}

function buildRoleInterestsLabel(claim: PublicClaim<string[]>): string | null {
  const roles = dedupeCaseInsensitive(
    (claim.value ?? []).map((role) => role?.trim()).filter((role): role is string => !!role),
  );
  if (roles.length === 0) {
    return null;
  }
  return `Interested in ${roles.slice(0, 2).join(' or ')}`;
}

function buildTargetIndustriesLabel(claim: PublicClaim<string[]>): string | null {
  const industries = (claim.value ?? [])
    .map((industry) => industry?.trim())
    .filter((industry): industry is string => !!industry);
  if (industries.length === 0) {
    return null;
  }
  return `Targeting: ${industries[0]}`;
}

function buildResponsivenessDrafts(claim: PublicClaim<Responsiveness>): ChipDraft[] {
  switch (claim.value) {
    case 'high':
      return [{ field: 'responsiveness', label: 'Historically responsive' }];
    // A poor track record is an unfavorable signal — flag it caution, unlike the positive case.
    case 'low':
    case 'non_responsive':
      return [{ field: 'responsiveness', label: 'Slow to respond', variant: 'negative' }];
    // 'medium' and an absent value carry no clear signal — no chip.
    default:
      return [];
  }
}

// The date carries the "last" meaning, so the label needs no "Last" prefix; e.g. "Open to work · Oct '25".
function buildLastOpenLabel(claim: PublicClaim<string>): string | null {
  const shortDate = shortMonthYear(claim.value);
  if (shortDate) {
    return `Open to work ${MIDDOT} ${shortDate}`;
  }
  return trimmedOrNull(claim.displayText);
}

// A label, or nothing — the shape most fields share.
function labelDraft(field: ChipField, label: string | null): ChipDraft[] {
  return label ? [{ field, label }] : [];
}

export function buildInsightChips(
  insights: CandidateInsights,
  prioritizedFields: ReadonlySet<ChipField> = new Set(),
): ChipSpec[] {
  const chips: ChipSpec[] = [];

  const consume = <T>(
    claim: PublicClaim<T> | undefined,
    build: (claim: PublicClaim<T>) => ChipDraft[],
  ): void => {
    if (!claim) {
      return;
    }
    for (const draft of build(claim)) {
      chips.push(finalizeChip(draft, claim));
    }
  };

  consume(insights.pay, buildPayDrafts);
  consume(insights.shifts, buildShiftDrafts);
  consume(insights.commute, (claim) => labelDraft('commute', buildCommuteLabel(claim)));
  consume(insights.relocation, buildRelocationDrafts);
  consume(insights.travel, buildTravelDrafts);
  consume(insights.languages, (claim) => labelDraft('languages', buildLanguageLabel(claim)));
  consume(insights.roleInterests, (claim) =>
    labelDraft('roleInterests', buildRoleInterestsLabel(claim)),
  );
  consume(insights.targetIndustries, (claim) =>
    labelDraft('targetIndustries', buildTargetIndustriesLabel(claim)),
  );
  consume(insights.responsiveness, buildResponsivenessDrafts);
  consume(insights.lastOpenToOpportunities, (claim) =>
    labelDraft('lastOpenToOpportunities', buildLastOpenLabel(claim)),
  );

  // Prioritized fields first, the fixed order within each group.
  const rank = (field: ChipField) => {
    const order = PRIORITY.indexOf(field);
    return prioritizedFields.has(field) ? order : order + PRIORITY.length;
  };
  return chips.sort((a, b) => rank(a.field) - rank(b.field)).slice(0, MAX_CHIPS);
}
